import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  RiMore2Line,
  RiEditLine,
  RiDeleteBinLine,
  RiEyeLine,
  RiSearchLine,
  RiCalendarLine,
  RiImageLine,
  RiAddLine,
} from "@remixicon/react";
import api from "@/utils/api";
import { useCurrentStore } from "@/hooks/useCurrentStore";
import { getCurrencySymbol } from "@/utils/currency";
import { Skeleton } from "@/components/ui/skeleton";
import { SectionCard } from "@/components/dashboard/shared/SectionCard";
import { dashboardStyles } from "@/components/dashboard/shared/dashboard-styles";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { statusBadgeClass } from "@/lib/status";

interface BookingSlot {
  _id: string;
  name: string;
  description: string;
  price: number;
  compareAtPrice?: number;
  category?: string;
  bookingType: 'car_hire' | 'apartment' | 'hotel' | 'service' | 'other';
  duration: string;
  capacity: number;
  images: Array<{
    url: string;
    alt?: string;
    isDefault?: boolean;
  }>;
  status: "draft" | "active" | "archived";
  createdAt: string;
  updatedAt: string;
}

interface BookingSlotListFilters {
  search: string;
  category: string;
  bookingType: string;
  status: string;
}

const BOOKING_TYPE_LABELS: Record<string, string> = {
  car_hire: 'Car Hire',
  apartment: 'Apartment',
  hotel: 'Hotel',
  service: 'Service',
  other: 'Other',
};

export function BookingSlotList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { storeId } = useCurrentStore();
  const [filters, setFilters] = useState<BookingSlotListFilters>({
    search: "",
    category: "all",
    bookingType: "all",
    status: "all",
  });
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [deleteSlotId, setDeleteSlotId] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const { data: slotsData, isLoading } = useQuery({
    queryKey: ["booking-slots", filters, sortBy, sortOrder, page, storeId],
    queryFn: async () => {
      const params = new URLSearchParams();

      if (filters.search) params.append("search", filters.search);
      if (filters.category !== "all")
        params.append("category", filters.category);
      if (filters.bookingType !== "all")
        params.append("bookingType", filters.bookingType);
      if (filters.status !== "all") params.append("status", filters.status);

      params.append("sortBy", sortBy);
      params.append("sortOrder", sortOrder);
      params.append("page", page.toString());
      params.append("limit", "15");

      const { data } = await api.get(`/api/booking-slots?${params}`);
      return data;
    },
    enabled: !!storeId,
  });

  const deleteSlotMutation = useMutation({
    mutationFn: async (slotId: string) => {
      return api.delete(`/api/booking-slots/${slotId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["booking-slots"] });
      toast.success("Booking slot deleted successfully");
      setDeleteSlotId(null);
      setShowDeleteDialog(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to delete booking slot");
    },
  });

  const slots = slotsData?.slots || [];
  const pagination = slotsData?.pagination || { page: 1, limit: 15, total: 0, pages: 1 };
  const currencySymbol = getCurrencySymbol('NGN');

  const getStatusColor = statusBadgeClass;

  const handleDelete = () => {
    if (deleteSlotId) {
      deleteSlotMutation.mutate(deleteSlotId);
    }
  };

  if (isMobile) {
    return (
      <div className="space-y-4">
        <SectionCard>
          <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <RiSearchLine className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search slots..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className={`${dashboardStyles.filterInput} pl-9`}
            />
          </div>
          <Select
            value={filters.status}
            onValueChange={(value) => setFilters({ ...filters, status: value })}
          >
            <SelectTrigger className={`${dashboardStyles.filterSelect} w-full sm:w-[180px]`}>
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="archived">Archived</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className={dashboardStyles.listItem}>
                <div className="w-full">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1">
                      <Skeleton className="h-4 w-36 mb-1.5" />
                      <Skeleton className="h-3 w-52" />
                    </div>
                    <Skeleton className="h-7 w-7 rounded-md" />
                  </div>
                  <div className="flex gap-2">
                    <Skeleton className="h-5 w-16 rounded-full" />
                    <Skeleton className="h-5 w-20 rounded-full" />
                    <Skeleton className="h-4 w-14" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : slots.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 px-4 rounded-xl bg-muted/20">
            <div className={dashboardStyles.emptyIcon}>
              <RiCalendarLine className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className={dashboardStyles.emptyTitle}>No booking slots found</p>
            <p className={dashboardStyles.emptyDescription}>
              Create your first slot to start accepting bookings.
            </p>
          </div>
        ) : (
          <div className={dashboardStyles.listContainer}>
            {slots.map((slot: BookingSlot) => (
              <div key={slot._id} className={dashboardStyles.listItem}>
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className={dashboardStyles.listItemIcon}>
                    <RiCalendarLine className={dashboardStyles.listItemIconSvg} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-sm leading-tight truncate">
                      {slot.name}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                      {slot.description}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap mt-1">
                      <Badge className={getStatusColor(slot.status)}>
                        {slot.status}
                      </Badge>
                      <Badge variant="outline">
                        {BOOKING_TYPE_LABELS[slot.bookingType] || slot.bookingType}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 ml-2">
                  <span className="text-sm font-semibold font-heading">
                    {currencySymbol}{slot.price}
                  </span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <RiMore2Line className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/dashboard/booking-slots/${slot._id}`)}>
                        <RiEyeLine className="h-4 w-4 mr-2" />
                        View
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(`/dashboard/booking-slots/${slot._id}/edit`)}>
                        <RiEditLine className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => {
                          setDeleteSlotId(slot._id);
                          setShowDeleteDialog(true);
                        }}
                        className="text-destructive"
                      >
                        <RiDeleteBinLine className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        )}
          <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Booking Slot</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this booking slot? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          </div>
        </SectionCard>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SectionCard>
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <RiSearchLine className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search slots..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className={`${dashboardStyles.filterInput} pl-9`}
              />
            </div>
            <Select
              value={filters.bookingType}
              onValueChange={(value) => setFilters({ ...filters, bookingType: value })}
            >
              <SelectTrigger className={`${dashboardStyles.filterSelect} w-full sm:w-[180px]`}>
                <SelectValue placeholder="Booking Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="car_hire">Car Hire</SelectItem>
                <SelectItem value="apartment">Apartment</SelectItem>
                <SelectItem value="hotel">Hotel</SelectItem>
                <SelectItem value="service">Service</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.status}
              onValueChange={(value) => setFilters({ ...filters, status: value })}
            >
              <SelectTrigger className={`${dashboardStyles.filterSelect} w-full sm:w-[180px]`}>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className={dashboardStyles.tableWrap}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Slot</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-10 w-10 rounded-lg flex-shrink-0" />
                          <div>
                            <Skeleton className="h-4 w-32 mb-1.5" />
                            <Skeleton className="h-3 w-48" />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell><Skeleton className="h-5 w-20 rounded-full" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-5 w-16 rounded-full" /></TableCell>
                      <TableCell className="text-right"><Skeleton className="h-7 w-7 rounded-md ml-auto" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : slots.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4 rounded-xl bg-muted/20">
              <div className={dashboardStyles.emptyIcon}>
                <RiCalendarLine className="h-4 w-4 text-muted-foreground" />
              </div>
              <p className={dashboardStyles.emptyTitle}>No booking slots found</p>
              <p className={dashboardStyles.emptyDescription}>
                Create your first slot to start accepting bookings from customers.
              </p>
            </div>
          ) : (
            <>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Slot</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {slots.map((slot: BookingSlot) => (
                  <TableRow key={slot._id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {slot.images?.[0] ? (
                          <img
                            src={slot.images[0].url}
                            alt={slot.name}
                            className="h-10 w-10 rounded object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                            <RiImageLine className="h-4 w-4 text-muted-foreground" />
                          </div>
                        )}
                        <div>
                          <div className="font-medium">{slot.name}</div>
                          <div className="text-sm text-muted-foreground line-clamp-1">
                            {slot.description}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {BOOKING_TYPE_LABELS[slot.bookingType] || slot.bookingType}
                      </Badge>
                    </TableCell>
                    <TableCell>{slot.duration}</TableCell>
                    <TableCell className="font-medium">
                      {currencySymbol}{slot.price}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(slot.status)}>
                        {slot.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <RiMore2Line className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/dashboard/booking-slots/${slot._id}`)}>
                            <RiEyeLine className="h-4 w-4 mr-2" />
                            View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/dashboard/booking-slots/${slot._id}/edit`)}>
                            <RiEditLine className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => {
                              setDeleteSlotId(slot._id);
                              setShowDeleteDialog(true);
                            }}
                            className="text-destructive"
                          >
                            <RiDeleteBinLine className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {pagination.pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to{" "}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
                {pagination.total} slots
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                  disabled={page === pagination.pages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
            </>
          )}

          <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Booking Slot</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this booking slot? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </SectionCard>
    </div>
  );
}

